import * as apiPost from 'apiAuto/backend/paths/PostAccessoryV1';
import * as apiPut from 'apiAuto/backend/paths/PutAccessoryIdV1';
import { Characteristic } from 'enums/characteristic';
import { MEDIACOLLECTION } from 'enums/mediaCollection';
import { uploadMedia } from 'functions/uploadMedia';
import * as Accessory from 'types/Accessory/Detail/Accessory';
import * as CategoryList from 'types/Accessory/Detail/CategoryList';
import * as CategoryTree from 'types/Accessory/Detail/CategoryTree';
import * as Co2Label from 'types/Accessory/Detail/Co2Label';
import * as Co2List from 'types/Accessory/Detail/Co2List';
import * as Image from 'types/Accessory/Detail/Image';
import * as UnitWeightList from 'types/Accessory/Detail/UnitWeightList';
import * as Synonyms from 'types/Synonym/Synonyms';
import * as TagList from 'types/_general/Store/TagList';
import * as Translation from 'types/_general/Translation';

const defaultCultureCode = 'de-DE';

export type Type = {
	carbonDioxide: Co2List.Type;
	carbonDioxideLabel: Co2Label.Type | null;
	carbonDioxideValue: number | null;
	categories: CategoryList.Type;
	characteristic: Characteristic | null;
	density: number | null;
	id: string | null;
	image: Image.Type | null;
	isPriceRecipeRelevant: boolean;
	namePlural: Translation.Type<string>;
	nameSingular: Translation.Type<string>;
	price: number | null;
	selectedCultureCode: string;
	source: string | null;
	synonyms: Synonyms.Type;
	tagIds: string[];
	unitWeights: UnitWeightList.Type;
};

export function create(): Type {
	return {
		carbonDioxide: [],
		carbonDioxideLabel: null,
		carbonDioxideValue: 0,
		categories: [],
		characteristic: null,
		density: 0,
		id: null,
		image: null,
		isPriceRecipeRelevant: false,
		namePlural: Translation.create(),
		nameSingular: Translation.create(),
		price: 0,
		selectedCultureCode: defaultCultureCode,
		source: null,
		synonyms: Synonyms.create(),
		tagIds: [],
		unitWeights: [],
	};
}

export function setName(
	state: Type,
	input: { cultureCode: string; value: string; key: 'nameSingular' | 'namePlural' }
): Type {
	if (!input) return state;
	if (!input.cultureCode) return state;
	return {
		...state,
		[input.key]: Translation.change(state[input.key], input.cultureCode, input.value),
	};
}

export function setDensity(state: Type, value: number): Type {
	return { ...state, density: value };
}

export function setPrice(state: Type, value: number): Type {
	return { ...state, price: value };
}

export function setCharacteristic(state: Type, value: Characteristic): Type {
	return { ...state, characteristic: value };
}

export function setSelectedCultureCode(state: Type, cultureCode: string): Type {
	return { ...state, selectedCultureCode: cultureCode };
}

export function setUnitWeights(state: Type, input: UnitWeightList.Type): Type {
	return { ...state, unitWeights: input };
}

export function setCategories(state: Type, input: CategoryTree.Type[]): Type {
	return {
		...state,
		categories: input.map((e: CategoryTree.Type) => ({
			id: e.id,
			name: e.name,
		})) as CategoryList.Type,
	};
}

export function setTagIds(state: Type, ids: string[]): Type {
	return { ...state, tagIds: ids };
}

export function addSynonym(state: Type): Type {
	const resultSynonyms = state.synonyms;
	if (!resultSynonyms[state.selectedCultureCode]) resultSynonyms[state.selectedCultureCode] = [];
	resultSynonyms[state.selectedCultureCode].push({
		id: null,
		nameSingular: '',
		namePlural: '',
	});
	return { ...state, synonyms: resultSynonyms };
}

export function deleteSynonym(state: Type, index: number): Type {
	const resultSynonyms = state.synonyms;
	resultSynonyms[state.selectedCultureCode].splice(index, 1);
	return { ...state, synonyms: resultSynonyms };
}

export function changeSynonym(
	state: Type,
	input: { index: number; text: string; key: 'nameSingular' | 'namePlural' }
): Type {
	const resultSynonyms = state.synonyms;
	resultSynonyms[state.selectedCultureCode][input.index][input.key] = input.text;
	return { ...state, synonyms: resultSynonyms };
}

export function load(_state: Type, input: Type): Type {
	return input;
}

export async function save(state: Type, cultureCode: string): Promise<Type> {
	if (state.id) {
		const newState = await Accessory.putToApi(state.id, mapToApi(state));

		return newState
			.map((x) => Accessory.mapToState(Accessory.mapFromApi(x), cultureCode))
			.getOrDefault(state);
	}
	const id = await Accessory.postToApi(mapToApi(state));
	return {
		...state,
		id: id,
	};
}

export async function translateAllNames(
	state: Type,
	input: { fromCultureCode: string } | undefined
): Promise<Type> {
	if (!input) return state;
	if (!input.fromCultureCode) return state;
	if (input.fromCultureCode === state.selectedCultureCode) return state;
	const translatedNameSingular: string = await Translation.getTranslationFromApi({
		fromCultureCode: input.fromCultureCode,
		toCultureCode: state.selectedCultureCode,
		text: state.nameSingular[input.fromCultureCode],
	});
	const translatedNamePlural: string = await Translation.getTranslationFromApi({
		fromCultureCode: input.fromCultureCode,
		toCultureCode: state.selectedCultureCode,
		text: state.namePlural[input.fromCultureCode],
	});
	return {
		...state,
		nameSingular: Translation.change(
			state.nameSingular,
			state.selectedCultureCode,
			translatedNameSingular
		),
		namePlural: Translation.change(
			state.namePlural,
			state.selectedCultureCode,
			translatedNamePlural
		),
	};
}

export async function translateSingleName(
	state: Type,
	input: { fromCultureCode: string; text: string; key: 'nameSingular' | 'namePlural' } | undefined
): Promise<Type> {
	if (!input) return state;
	if (!input.fromCultureCode) return state;
	if (input.fromCultureCode === state.selectedCultureCode) return state;
	const translatedText: string = await Translation.getTranslationFromApi({
		fromCultureCode: input.fromCultureCode,
		toCultureCode: state.selectedCultureCode,
		text: input.text,
	});

	return {
		...state,
		[input.key]: Translation.change(
			state[input.key],
			state.selectedCultureCode,
			translatedText
		),
	};
}

export async function uploadImage(state: Type, input: FormData | null | undefined): Promise<Type> {
	if (!input) return state;
	return await uploadMedia<Type>(state, input, MEDIACOLLECTION.Accessory);
}

export async function getAndSetSuggestedTags(
	state: Type,
	cultureCode: string | undefined
): Promise<Type> {
	if (!cultureCode) return state;
	const suggestedTags = await TagList.getFromApiSuggested(
		state.nameSingular[cultureCode],
		cultureCode
	);
	const newTagIds: string[] = TagList.applySuggestedTags(suggestedTags, state.tagIds);
	return { ...state, tagIds: newTagIds };
}

function mapToApi(state: Type): apiPut.RequestBody & apiPost.RequestBody {
	return {
		...state,
		categories: state.categories.map((e) => {
			return e.id;
		}),
		characteristic: state.characteristic ?? Characteristic.SolidQuantifiable,
		imageId: state.image?.id ?? null,
		namePluralTranslations: state.namePlural,
		nameSingularTranslations: state.nameSingular,
		tags: state.tagIds,
		unitWeights: UnitWeightList.mapToApi(state.unitWeights),
		currencyCode: null,
	};
}
